import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, Position, NodeProps } from 'reactflow';
import { FiMessageSquare, FiX } from 'react-icons/fi';

interface AnnotationData {
  text: string;
  color: string;
  onUpdate: (text: string, color: string) => void;
  onDelete: () => void;
}

const AnnotationNode: React.FC<NodeProps<AnnotationData>> = ({ data, selected }) => {
  const { t } = useTranslation('canvas');
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(data.text);
  const [color, setColor] = useState(data.color || '#FEF3C7');

  const handleSave = () => {
    data.onUpdate(text, color);
    setEditing(false);
  };

  const colors = [
    { name: 'Jaune', value: '#FEF3C7' },
    { name: 'Bleu', value: '#DBEAFE' },
    { name: 'Vert', value: '#D1FAE5' },
    { name: 'Rose', value: '#FCE7F3' },
    { name: 'Orange', value: '#FED7AA' },
  ];

  return (
    <div
      style={{
        background: color,
        border: selected ? '2px solid #3B82F6' : '1px solid #E5E7EB',
        borderRadius: 8,
        padding: 12,
        minWidth: 200,
        maxWidth: 300,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <FiMessageSquare size={16} style={{ color: '#6B7280' }} />
        <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Note</span>
        <button
          onClick={data.onDelete}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <FiX size={16} style={{ color: '#6B7280' }} />
        </button>
      </div>

      {editing ? (
        <div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{
              width: '100%',
              minHeight: 60,
              padding: 8,
              fontSize: 13,
              border: '1px solid #D1D5DB',
              borderRadius: 4,
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
            {colors.map((c) => (
              <button
                key={c.value}
                onClick={() => setColor(c.value)}
                style={{
                  width: 24,
                  height: 24,
                  background: c.value,
                  border: color === c.value ? '2px solid #3B82F6' : '1px solid #D1D5DB',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
                title={c.name}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={handleSave}
              style={{
                flex: 1,
                padding: '6px 12px',
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {t('annotationNode.save')}
            </button>
            <button
              onClick={() => {
                setText(data.text);
                setColor(data.color || '#FEF3C7');
                setEditing(false);
              }}
              style={{
                flex: 1,
                padding: '6px 12px',
                background: '#F3F4F6',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {t('annotationNode.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => setEditing(true)}
          style={{
            fontSize: 13,
            color: '#374151',
            cursor: 'text',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            minHeight: 40,
          }}
        >
          {text || t('annotationNode.placeholder')}
        </div>
      )}
    </div>
  );
};

export default AnnotationNode;
